import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import useToastContext from "hooks/useToastContext";
import { apiGet, apiPost, apiDelete } from "../../../utils/apiFetch";
import apiPath from "../../../utils/apiPath";
import dayjs from "dayjs";
import { AiFillEye, AiOutlineFilePdf, AiOutlineDelete } from "react-icons/ai";
import { BsArrowUpShort } from "react-icons/bs";
import Pagination from "pages/Pagination";
import ODateRangePicker from 'components/shared/datePicker/ODateRangePicker';

// 🛠️ FIXED: Credibled/Certn returns human-readable scores like "Cleared", "Clear",
// "Consider", "Hit", "Fail" — never the literal strings 'PASS' / 'FAIL' that the
// old code checked for. Matching is now case-insensitive and substring-based.
const CLEAR_SCORES = ['clear', 'cleared', 'pass', 'passed'];
const FAIL_SCORES = ['fail', 'failed', 'hit', 'hits'];

const BackgroundCheckList = ({ onViewCheck, existingChecks, setExistingChecks, stats, setStats }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const refreshLoadingRef = useRef({});
  const [refreshLoading, setRefreshLoading] = useState({});
  const [loading, setLoading] = useState(false);

  // --- Filter States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterData, setFilterData] = useState({
    searchKey: '',
    startDate: '',
    endDate: '',
    status: 'All'
  });
  const [isDatePickerReset, setIsDatePickerReset] = useState(false); // 🆕 reset flag for date picker

  // --- Pagination & Sorting States ---
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  const [sort, setSort] = useState({
    sortBy: 'orderedAt',
    sortType: 'desc'
  });

  // ✅ Prevents double calls in Strict Mode and when resetting page
  const isMounted = useRef(false);
  const prevPage = useRef(1);

  // --- Core Fetch Function ---
  const loadExistingChecks = useCallback(async () => {
    setLoading(true);
    try {
      const { startDate, endDate, status, searchKey } = filterData;

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sort.sortBy,
        sortType: sort.sortType,
        keyword: searchKey,
        startDate: startDate ? dayjs(startDate).format('YYYY-MM-DD') : '',
        endDate: endDate ? dayjs(endDate).format('YYYY-MM-DD') : '',
        status: status !== 'All' ? status : '',
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined || params[key] === null) {
          delete params[key];
        }
      });

      const path = apiPath.backgroundCheck;
      const result = await apiGet(path, params);

      if (result?.data?.success) {
        const checks = result.data.results?.checks || result.data.data?.checks || result.data.checks || [];
        const paginationData = result.data.results?.pagination || result.data.data?.pagination || result.data.pagination;

        const validatedChecks = checks.map(check => ({
          ...check,
          _id: check._id || check.id,
          status: check.status || 'pending',
          orderedAt: check.orderedAt || new Date().toISOString(),
        }));

        setExistingChecks(validatedChecks);

        if (paginationData) {
          setPagination({
            page: paginationData.page || 1,
            limit: paginationData.limit || 10,
            total: paginationData.total || validatedChecks.length,
            pages: paginationData.pages || 1
          });
        }

        // 🟢 Stats Calculation
        const totalCount = paginationData?.total || validatedChecks.length;
        const completedCount = validatedChecks.filter(c => c.status === 'Complete').length;
        const inProgressCount = validatedChecks.filter(c => c.status === 'In Progress' || c.status === 'Analyzing').length;
        const actionRequiredCount = validatedChecks.filter(c => c.status === 'Action Required').length;

        setStats({
          total: totalCount,
          completed: completedCount,
          inProgress: inProgressCount,
          actionRequired: actionRequiredCount
        });
      } else {
        notification.error(result?.data?.message || "Failed to load background checks");
      }
    } catch (error) {
      console.error("Error loading background checks:", error);
      notification.error("Failed to load background checks");
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, sort.sortBy, sort.sortType, filterData, notification, setExistingChecks, setStats]);

  // ✅ Effect 1: Load only ONCE on initial mount
  useEffect(() => {
    if (isMounted.current) return;
    isMounted.current = true;
    loadExistingChecks();
  }, [loadExistingChecks]);

  // ✅ Effect 2: Refetch when filters / sort / pagination change.
  // The isMounted guard above already prevents the mount double-fetch,
  // so this effect can simply always fetch on any dependency change.
  // 🛠️ FIXED: removed the fragile value-comparison branch that was
  // silently skipping refetches when only status/dates changed.
  useEffect(() => {
    if (!isMounted.current) return;

    loadExistingChecks();
    prevPage.current = pagination.page;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.page,
    sort.sortBy,
    sort.sortType,
    debouncedSearchTerm,
    filterData.startDate,
    filterData.endDate,
    filterData.status,
    filterData.searchKey
  ]);

  // --- Debounce Search Input ---
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  useEffect(() => {
    setFilterData(prev => ({
      ...prev,
      searchKey: debouncedSearchTerm
    }));
  }, [debouncedSearchTerm]);

  // --- Handlers ---
  const handleDateChange = (start, end) => {
    setIsDatePickerReset(false); // 🆕 user picked dates → clear the reset flag
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilterData(prev => ({
      ...prev,
      startDate: start,
      endDate: end
    }));
  };

  const handleStatusChange = (e) => {
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilterData(prev => ({
      ...prev,
      status: e.target.value
    }));
  };

  const handleReset = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setIsDatePickerReset(true); // 🆕 tell the date picker to clear itself
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilterData({
      searchKey: '',
      startDate: '',
      endDate: '',
      status: 'All'
    });
    setSort({
      sortBy: 'orderedAt',
      sortType: 'desc'
    });
    // No manual loadExistingChecks() here — Effect 2 will fire once
    // because filterData / sort / pagination.page all changed.
  };

  const handleRefreshCheck = async (checkId) => {
    setRefreshLoading(prev => ({ ...prev, [checkId]: true }));
    try {
      const path = `${apiPath.backgroundCheck}/${checkId}/refresh`;
      const result = await apiGet(path);
      if (result?.data?.success) {
        notification.success("Background check refreshed successfully!");
        loadExistingChecks();
      } else {
        notification.error(result?.data?.message || "Failed to refresh background check");
      }
    } catch (error) {
      notification.error("Failed to refresh background check");
    } finally {
      setRefreshLoading(prev => ({ ...prev, [checkId]: false }));
    }
  };

  const handleSort = (sortByValue) => {
    const newSortType = sort.sortBy === sortByValue && sort.sortType === "asc" ? "desc" : "asc";
    setSort({ sortBy: sortByValue, sortType: newSortType });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageClick = (event) => {
    const newPage = event.selected + 1;
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleDeleteCheck = async (checkId) => {
    if (!window.confirm("Are you sure you want to delete this background check? This action cannot be undone.")) return;
    try {
      const path = `${apiPath.backgroundCheck}/${checkId}`;
      const result = await apiDelete(path);
      if (result?.data?.success) {
        notification.success("Background check deleted successfully!");
        loadExistingChecks();
      } else {
        notification.error(result?.data?.message || "Failed to delete background check");
      }
    } catch (error) {
      notification.error("Failed to delete background check");
    }
  };

  // --- Status Color Helpers ---
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status) {
        case 'Complete': return 'bg-green-100 text-green-800';
        case 'In Progress':
        case 'Analyzing': return 'bg-blue-100 text-blue-800';
        case 'Waiting On Candidate':
        case 'Action Required': return 'bg-yellow-100 text-yellow-800';
        case 'Cancelled':
        case 'In Dispute': return 'bg-red-100 text-red-800';
        default: return 'bg-gray-100 text-gray-800';
    }
  };

  // 🛠️ FIXED: was exact-match 'clear' against a value like "Cleared" (never matches).
  // Now substring-matched, case-insensitively, and recognizes 'Partial' — the value
  // the backend stores when a multi-check order has a mix of results.
  const getResultColor = (resultStatus) => {
    if (!resultStatus) return 'bg-gray-100 text-gray-800';
    const s = resultStatus.toLowerCase();
    if (FAIL_SCORES.some(f => s.includes(f))) return 'bg-red-100 text-red-800';
    if (CLEAR_SCORES.some(c => s.includes(c))) return 'bg-green-100 text-green-800';
    if (s.includes('partial') || s.includes('unknown') || s.includes('consider')) return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  // 🛠️ FIXED: previously compared scan.score === 'PASS' / 'FAIL' exactly, which never
  // matches Credibled's real values (e.g. "Cleared"), so everything fell through to Pending.
  const renderSimplifiedResult = (resultData) => {
    if (!resultData) return <span className="text-yellow-500 font-bold">Pending</span>;

    if (typeof resultData === 'string') return resultData;

    if (Array.isArray(resultData) && resultData.length > 0) {
      const scores = resultData
        .map(scan => (scan.score || '').toString().toLowerCase())
        .filter(Boolean);

      if (scores.length === 0) {
        return <span className="text-yellow-500 font-bold">Pending</span>;
      }

      const hasFail = scores.some(s => FAIL_SCORES.some(f => s.includes(f)));
      const allClear = scores.every(s => CLEAR_SCORES.some(c => s.includes(c)));
      const anyClear = scores.some(s => CLEAR_SCORES.some(c => s.includes(c)));

      if (hasFail) {
        return <span className="text-red-600 font-bold">Fail</span>;
      } else if (allClear) {
        return <span className="text-green-600 font-bold">Pass</span>;
      } else if (anyClear) {
        return <span className="text-yellow-600 font-bold">Partial</span>;
      }
    }

    return <span className="text-yellow-500 font-bold">Pending</span>;
  };

  return (
    <div className="bg-[#F9F9F9]">
      <div className="bg-white rounded-lg border border-[#E9EDF9]">

        {/* Filter Bar */}
        <form className='border-b border-b-[#E3E3E3] 2xl:flex gap-2 px-4 py-3 flex-wrap justify-between'>
          <div className='col-span-2 flex flex-wrap items-center mb-2 2xl:mb-0'>
            <div className='flex items-center lg:pt-0 pt-3 flex-wrap'>

              {/* 1. Search Input */}
              <div className='relative flex items-center mb-3'>
                <div className='absolute inset-y-0 right-0 flex items-center pl-3 mr-3 pointer-events-none'>
                  {!searchTerm ? (
                    <svg aria-hidden='true' className='w-4 h-4 text-[#A5A5A5]' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' />
                    </svg>
                  ) : ( '' )}
                </div>
                <input
                  type='search'
                  className='block w-full p-2 outline-none text-sm text-gray-900 2xl:min-w-[300px] xl:min-w-[250px] rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500'
                  placeholder='Search name or email...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>

              {/* 2. Date Range Picker */}
              <ODateRangePicker
                handleDateChange={handleDateChange}
                isReset={isDatePickerReset}
                setIsReset={setIsDatePickerReset}
              />

              {/* 3. Status Dropdown */}
              <div className="flex items-center mb-3 ml-3">
                <select
                  className="block p-2.5 w-[180px] text-sm text-[#A5A5A5] bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                  value={filterData.status}
                  onChange={handleStatusChange}
                >
                  <option key="All" value="All">All Status</option>
                  <option key="Waiting On Candidate" value="Waiting On Candidate">Waiting On Candidate</option>
                  <option key="In Progress" value="In Progress">In Progress</option>
                  <option key="Complete" value="Complete">Complete</option>
                  <option key="Action Required" value="Action Required">Action Required</option>
                  <option key="Cancelled" value="Cancelled">Cancelled</option>
                  <option key="In Dispute" value="In Dispute">In Dispute</option>
                </select>
              </div>

              {/* 4. Reset Button */}
              <button
                onClick={handleReset}
                className="ml-3 mb-3 bg-LightBlue text-white font-medium text-sm px-6 py-2.5 rounded shadow hover:shadow-lg transition-all"
                style={{ borderRadius: '0.5rem' }}
                type="button"
              >
                Reset
              </button>
            </div>
          </div>
        </form>

        {/* Table Section */}
        <div className="p-3">
          {loading ? null : (
            <>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-sm text-left text-gray-600">
                  <thead className="text-xs text-gray-900 border bg-[#E1E6EE]">
                    <tr>
                      <th className="py-3 px-6">SR No</th>
                      <th className="py-3 px-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort("employeeData.firstName")}>
                        <div className="flex items-center">
                          <span>Candidate Name</span>
                          {sort.sortBy === "employeeData.firstName" && ( <BsArrowUpShort className={`w-4 h-4 ${sort.sortType === "desc" ? "rotate-180" : ""}`} /> )}
                        </div>
                      </th>
                      <th className="py-3 px-6">Email</th>
                      <th className="py-3 px-6">Order ID</th>
                      <th className="py-3 px-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort("orderedAt")}>
                        <div className="flex items-center">
                          <span>Ordered Date</span>
                          {sort.sortBy === "orderedAt" && ( <BsArrowUpShort className={`w-4 h-4 ${sort.sortType === "desc" ? "rotate-180" : ""}`} /> )}
                        </div>
                      </th>
                      <th className="py-3 px-6 cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort("status")}>
                        <div className="flex items-center">
                          <span>Status</span>
                          {sort.sortBy === "status" && ( <BsArrowUpShort className={`w-4 h-4 ${sort.sortType === "desc" ? "rotate-180" : ""}`} /> )}
                        </div>
                      </th>
                      <th className="py-3 px-6">Result</th>
                      <th className="py-3 px-6 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingChecks?.map((check, index) => (
                      <tr key={check._id} className="bg-white border-b hover:bg-gray-50">
                        <td className="py-4 px-6 border-r">{(pagination.page - 1) * pagination.limit + index + 1}</td>
                        <td className="py-4 px-6 border-r text-gray-900">{check?.employeeData?.firstName || ''} {check?.employeeData?.lastName || ''}</td>
                        <td className="py-4 px-6 border-r">{check?.employeeData?.email || 'N/A'}</td>
                        <td className="py-4 px-6 border-r font-mono text-sm">{check?.applicationId || 'N/A'}</td>
                        <td className="py-4 px-6 border-r">{dayjs(check?.orderedAt).format("MM-DD-YYYY h:mm A")}</td>
                        <td className="py-4 px-6 border-r">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs ${getStatusColor(check.status)}`}>
                            {check.status || 'Unknown'}
                          </span>
                        </td>
                        <td className="py-4 px-6 border-r font-semibold">
                          {renderSimplifiedResult(check.resultData)}
                        </td>
                        <td className="py-4 px-6 border-l">
                          <div className="flex justify-center space-x-3">
                            <button onClick={() => onViewCheck(check)} className="p-2 text-blue-600 hover:text-blue-800"><AiFillEye className="w-5 h-5" /></button>
                            {/* {check.reportAvailable && (
                              <span className="p-2 text-red-600" title="Report available in details view">
                                <AiOutlineFilePdf className="w-5 h-5" />
                              </span>
                            )} */}
                            <button onClick={() => handleRefreshCheck(check._id)} disabled={refreshLoading[check._id]} className={`p-2 ${refreshLoading[check._id] ? 'text-gray-400' : 'text-gray-600 hover:text-gray-800'}`}>⟳</button>
                            {/* <button onClick={() => handleDeleteCheck(check._id)} className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"><AiOutlineDelete className="w-5 h-5" /></button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {existingChecks.length === 0 && (
                      <tr><td colSpan="9" className="py-8 text-center text-gray-500">No background checks found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.total > pagination.limit && (
                <div className="mt-6">
                  <Pagination page={pagination.page} handlePageClick={handlePageClick} options={{ page: pagination.page, pageCount: pagination.pages, totalItems: pagination.total, perPageItem: Math.min(pagination.limit, existingChecks.length) }} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BackgroundCheckList;